import { html, css } from 'lit';
import WarpElement from '@warp-ds/elements-core';
import pulse from '@finn-no/pulse-sdk';
import tracking from '@finn-no/analytics';
import { i18n } from '@lingui/core';

const converter = (value) => {
    if (!value) return null;

    try {
        const v = JSON.parse(value);
        if (Array.isArray(v)) {
            return v.map((val) => val.value || val);
        }
        if (v.value) {
            return [v.value];
        }
    } catch (err) {
        // noop
    }

    if (typeof value === 'string') {
        return [value];
    }

    return null;
};

function round(value) {
    return Math.round(value * 100000) / 100000;
}

function getCurrentPosition() {
    return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const coords = {
                        lat: round(pos.coords.latitude),
                        lon: round(pos.coords.longitude),
                    };
                    resolve(coords);
                },
                (e) => {
                    reject(
                        new Error(`Could not find position with error: ${e}`),
                    );
                },
                { timeout: 5000 },
            );
        } else {
            reject(new Error('Could not find position'));
        }
    });
}

function onClickClosest(event, el) {
    event.preventDefault();
    getCurrentPosition()
        .then((coords) => {
            if (coords) {
                window.location.href = `${el.href}&lat=${coords.lat}&lon=${coords.lon}`;
            } else {
                window.location.href = el.href;
            }
        })
        .catch(() => {
            window.location.href = el.href;
        });
}

class MFPRealestate extends WarpElement {
    static properties = {
        market: { attribute: true, type: String },
        baseurl: { attribute: true, type: String },
        css: { attribute: true, converter },
    };

    static styles = [
        WarpElement.styles,
        css`
            *,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0;box-sizing:border-box;border-style:solid;border-width:0;border-color:var(--w-s-color-border,var(--w-s-color-border-default))}html{font-size:62.5%}body{background-color:var(--w-s-color-background,var(--w-s-color-background-default));min-height:100%;margin:0;overflow-y:scroll}body,:host{-webkit-text-size-adjust:100%;tab-size:4;-webkit-tap-highlight-color:transparent;font-family:var(--w-font-family);font-size:var(--w-font-size-m);line-height:var(--w-line-height-m);color:var(--w-s-color-text,var(--w-s-color-text-default))}hr{color:inherit;border-top-width:1px;height:0}abbr:where([title]){-webkit-text-decoration:underline dotted;text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:700}a{cursor:pointer;color:var(--w-s-color-text-link);text-decoration:none}a:hover,a:focus,a:active{-webkit-text-decoration:var(--w-decoration-text-link);-webkit-text-decoration:var(--w-decoration-text-link);text-decoration:var(--w-decoration-text-link)}b,strong{font-weight:700}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace;font-size:1em}sub,sup{vertical-align:baseline;font-size:75%;line-height:0;position:relative}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;font-weight:inherit;line-height:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}button,[type=button],[type=reset],[type=submit]{-webkit-appearance:button}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button{height:auto}::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{margin:0;padding:0;list-style:none}textarea{resize:vertical}input::placeholder,textarea::placeholder{opacity:1;color:var(--w-s-color-text-placeholder)}button,[role=button]{cursor:pointer}:disabled{cursor:default}img,svg,video,canvas,audio,iframe,embed,object{vertical-align:middle;display:block}img,video{max-width:100%;height:auto}h1{font-size:var(--w-font-size-xxl);line-height:var(--w-line-height-xxl)}h2{font-size:var(--w-font-size-xl);line-height:var(--w-line-height-xl)}h3{font-size:var(--w-font-size-l);line-height:var(--w-line-height-l)}h4{font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}h5{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}dt,dd{margin:0 16px}h1,h2,h3,h4,h5,ul,ol,dl,p,blockquote{margin:0 0 8px}[hidden]{display:none!important}[tabindex="-1"]:focus:not(:focus-visible){outline:none}legend{float:left;width:100%;margin:0;padding:0;display:table}legend+*{clear:both}fieldset{border:0;min-width:0;margin:0;padding:.01em 0 0}body:not(:-moz-handler-blocked) fieldset{display:table-cell}.page-container{background-color:var(--w-s-color-background);max-width:1010px;margin:0;padding:0 16px}.h2{font-weight:700;font-size:var(--w-font-size-xl);line-height:var(--w-line-height-xl)}.h4{font-weight:700;font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}@media (min-width:1300px){.page-container{margin-left:auto;margin-right:auto;padding-left:31px;padding-right:31px}}.rounded-8{border-radius:8px}.opacity-0{opacity:0}.focus\\:opacity-100:focus{opacity:1}.block{display:block}.inline-block{display:inline-block}.flex{display:flex}.grid{display:grid}.no-underline{text-decoration:none}.items-center{align-items:center}.left-0{left:0}.top-0{top:0}.absolute{position:absolute}.relative{position:relative}.static{position:static}.focus\\:z-50:focus{z-index:50}.s-bg{background-color:var(--w-s-color-background)}.s-text-link{color:var(--w-s-color-text-link)}.s-icon-secondary{color:var(--w-s-color-icon-secondary)}.-mx-16{margin-left:-1.6rem;margin-right:-1.6rem}.last-child\\:mb-0>:last-child,.mb-0{margin-bottom:0}.mb-16{margin-bottom:1.6rem}.mb-24{margin-bottom:2.4rem}.mb-32{margin-bottom:3.2rem}.mb-8{margin-bottom:.8rem}.mr-8{margin-right:.8rem}.p-16{padding:1.6rem}.px-10{padding-left:1rem;padding-right:1rem}.py-4{padding-top:.4rem;padding-bottom:.4rem}.pr-0{padding-right:0}.pt-16{padding-top:1.6rem}.break-words{overflow-wrap:break-word}.text-s{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}@media (min-width:768px){.md\\:col-span-2{grid-column:span 2/span 2}.md\\:grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}};

            :host {
                display: block;
            }
        `,
    ];

    connectedCallback() {
        super.connectedCallback();

        const messages = JSON.parse(this.getAttribute('messages') || '{}');
        const locale = this.getAttribute('locale') || 'nb';
        i18n.load(locale, messages);
        i18n.activate(locale);

        pulse.trackPageView({
            object: {
                type: 'MarketFrontpage',
            },
            vertical: {
                name: 'realestate',
            },
        });

        tracking.trackPage({});

        if (navigator.serviceWorker) {
            navigator.serviceWorker.register('/sw.js').catch(() => { });
        }
    }

    trackRealestateHomes(event) {
        onClickClosest(event, event.target);
    }

    trackRealestateLeisure(event) {
        onClickClosest(event, event.target);
    }

    render() {
        return html`
            <a
                href="#market-start"
                class="s-text-link no-underline absolute top-0 left-0 opacity-0 focus:opacity-100 focus:z-50 py-4 px-10 s-bg"
                >${i18n.t({
            id: 'realestate.skiplink',
            message: 'Go to main content',
            comment:
                'skiplink shown when navigating the page with keyboard',
        })}</a
            >
            <slot name="topbar"></slot>
            <slot name="banner"></slot>
            <main class="page-container pt-16" style="min-height: 60vw">
                <slot name="broadcast"></slot>
                <header class="flex items-center mb-16">
                    <span class="s-icon-secondary mr-8">
                        <slot name="icon"></slot>
                    </span>
                    <h1
                        class="h2 mb-0"
                        data-automation-id="marketTitle"
                        id="market-start"
                    >
                        ${this.market}
                    </h1>
                </header>
                <div class="grid md:grid-cols-3">
                    <div class="md:col-span-2">
                        <div class="-mx-16 mb-32">
                            <slot name="links"></slot>
                        </div>
                        <slot name="info-panel"></slot>
                        <slot name="meglersiden-banner"></slot>
                    </div>
                    <div>
                        <section
                            class="block relative break-words p-16 mb-24 rounded-8"
                            aria-labeledby="popSearches"
                        >
                            <h2 class="h4" id="popSearches">
                                ${i18n.t({
            id: 'realestate.searches.title',
            message: 'Popular searches',
            comment:
                'title for the search section on the realestate market frontpage',
        })}
                            </h2>
                            <ul class="list mb-0 text-s">
                                <li>
                                    <a
                                        href="${this
                .baseurl}/realestate/homes/search.html?sort=CLOSEST"
                                        data-link-id="realestate-homes"
                                        @click=${this.trackRealestateHomes}
                                        class="inline-block py-4"
                                        >${i18n.t({
                    id: 'realestate.searches.homes',
                    message: 'Homes nearby',
                    comment:
                        'link to search for homes for sale nearby',
                })}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="${this
                .baseurl}/realestate/leisuresale/search.html?sort=CLOSEST"
                                        data-link-id="realestate-leisuresale"
                                        @click=${this.trackRealestateLeisure}
                                        class="inline-block py-4"
                                        >${i18n.t({
                    id: 'realestate.searches.leisure',
                    message: 'Leisure homes nearby',
                    comment: `link to search for leisure homes for sale nearby`,
                })}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="${this
                .baseurl}/realestate/lettings/search.html?property_type=16"
                                        class="inline-block py-4"
                                        >${i18n.t({
                    id: 'realestate.searches.rent',
                    message:
                        'Studio apartments for rent',
                    comment: `link to search for small homes / studio apartments for rent`,
                })}
                                    </a>
                                </li>
                            </ul>
                        </section>
                        <slot name="user-searches"></slot>
                        <section
                            class="group block relative break-words last-child:mb-0 p-16 pr-0 rounded-8"
                            aria-labeledbyt="nyttigeVerktoey"
                        >
                            <h2 class="h4 mb-8" id="nyttigeVerktoey">
                                ${i18n.t({
                    id: 'realestate.tools.title',
                    message: 'Useful tools',
                    comment:
                        'title for the tools section on the realestate market frontpage',
                })}
                            </h2>
                            <ul class="list text-s">
                                <li>
                                    <a
                                        href="${this
                .baseurl}/okonomi/sjekk/lan/bolig/"
                                        class="inline-block py-4"
                                        >${i18n.t({
                    id: 'realestate.tools.loan',
                    message: 'Loan calculator',
                    comment: `link to the loan calculator for homes`,
                })}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="${this
                .baseurl}/okonomi/sjekk/forsikring/bolig/"
                                        class="inline-block py-4"
                                        >${i18n.t({
                    id: 'realestate.tools.insurance',
                    message: 'Insurance your home',
                    comment: `link to insurance info for homes`,
                })}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="${this
                .baseurl}/eiendom/leiekontrakt.html"
                                        class="inline-block py-4"
                                        >${i18n.t({
                    id: 'realestate.tools.contract',
                    message:
                        'Rental contract for owners and tenants',
                    comment: `link to rental contract for owners and tenants`,
                })}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="http://www.meglersiden.no?utm_source=finn"
                                        class="inline-block py-4"
                                        >${i18n.t({
                    id: 'realestate.tools.meglersiden',
                    message:
                        'Get offers from realtors in your area',
                    comment: `link to meglersiden.no`,
                })}
                                    </a>
                                </li>
                            </ul>
                        </section>
                    </div>
                </div>
                <slot name="recommendations"></slot>
            </main>
            <slot name="footer"></slot>
        `;
    }
}

customElements.define('mfp-realestate', MFPRealestate);
